import React, { useCallback, useEffect, useRef, useMemo, useState } from 'react';
import { Button, Modal } from '@bettercomp/ui-components';
import { useAuthContext } from '@bettercomp/ams-spa-auth-react';
import { logout } from 'service/cognitoService';
import { appLogout } from 'service/appLogout';
import { createAuthBroadcastChannel, AuthBroadcastMessageType } from './constants';
import cn from './styles.module.scss';

const IDLE_LOGOUT_TIMEOUT = 30 * 60 * 1000;
const IDLE_WARNING_TIMEOUT = IDLE_LOGOUT_TIMEOUT - 3 * 60 * 1000;

export const IdleWarning = () => {
  const [visible, setVisible] = useState(false);
  const logoutTimeout = useRef<ReturnType<typeof setTimeout>>();
  const idleWarningTimeout = useRef<ReturnType<typeof setTimeout>>();
  const channel = useMemo(() => createAuthBroadcastChannel(), [])
  const { session } = useAuthContext();
  // Latest-ref: the armed 30-minute timeout must see the session that exists
  // when it fires, not the one captured when the timer was scheduled.
  const sessionRef = useRef(session);
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  const onLogoutTimeout = () => {
    // Mode-aware: an AMS session revokes and broadcasts on its own channel;
    // the idle Up/Logout messaging below stays as is either way.
    appLogout(sessionRef.current);
  };

  const onIdleWarningTimeout = () => {
    setVisible(true);
  };

  const resetTimer = useCallback((force: boolean = false) => {
    if (force || !visible) {

      if (idleWarningTimeout.current) {
        clearTimeout(idleWarningTimeout.current);
      }

      if (logoutTimeout.current) {
        clearTimeout(logoutTimeout.current);
      }

      idleWarningTimeout.current = setTimeout(onIdleWarningTimeout, IDLE_WARNING_TIMEOUT);
      logoutTimeout.current = setTimeout(onLogoutTimeout, IDLE_LOGOUT_TIMEOUT);
    }
  }, [visible]);

  useEffect(() => {
    resetTimer(true);
  }, [resetTimer]);

  useEffect(() => () => {
    channel.close();
  }, [channel]);

  useEffect(() => {
    // handle clicks, that comes from another tab
    const authChannelMessageHandler = (message: { data: AuthBroadcastMessageType }) => {
      if (message.data === AuthBroadcastMessageType.Up) {
        setVisible(false);
        resetTimer(true)
      }
      if (message.data === AuthBroadcastMessageType.Logout) {
        logout(false);
      }
    };

    const activityHandler = () => {
      // notify other tabs about clicks
      channel.postMessage(AuthBroadcastMessageType.Up);
      resetTimer();
    }

    channel.addEventListener('message', authChannelMessageHandler);
    document.addEventListener('click', activityHandler);

    return () => {
      channel.removeEventListener('message', authChannelMessageHandler);
      document.removeEventListener('click', activityHandler);
    };
  }, [channel, resetTimer]);

  const handleConfirm = async () => {
    setVisible(false);
    resetTimer(true);
  };

  return (
    <Modal
      open={visible}
      closable={false}
      centered
    >
      <div className={cn.container}>
        <div>Your session will expire soon</div>
        <Button type="primary" onClick={handleConfirm} size="large">Keep Me Logged in</Button>
      </div>
    </Modal>
  );
};
